import { FC } from 'react';
import { Divider, Grid, Stack, Typography, styled } from '@mui/material';
import { AttachFileOutlined } from '@mui/icons-material';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined';

import { CustomerJob } from 'domain/entities/CustomerJob';
import { Entity } from 'domain/types/Entity';
import { AttachmentFile } from 'domain/entities/Attachment';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { downloadFile } from 'infrastructure/utils/downloadFile';
import { IconButton, IconContainer, Modal } from 'targets/web/components';
import { componentShadows } from 'targets/web/theme/shadows';

const Container = styled(Stack)(({ theme }) => ({
  flex: '1 0 0',
  alignSelf: 'stretch',
  padding: theme.spacing(6),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,
  boxShadow: componentShadows.card,
}));

const NoAttachments: FC = () => {
  const t = useTranslationPrefix('customer_portal.requested_service_details_modal');
  return (
    <Container alignItems="center" justifyContent="center" gap={2}>
      <IconContainer padding={2}>
        <AttachFileOutlined sx={{ fontSize: '24px' }} />
      </IconContainer>

      <Typography variant="body1">{t('no_attachments')}</Typography>
    </Container>
  );
};

const AttachmentItem: FC<{
  attachment: AttachmentFile;
  onPreview: () => void;
  onDownload: () => void;
}> = ({ attachment, onPreview, onDownload }) => {
  return (
    <Stack direction="row" justifyContent="space-between" gap={2}>
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={{
          paddingInline: 2,
          paddingBlock: 1,
          borderRadius: 1,
          borderColor: 'outline',
          borderStyle: 'solid',
          borderWidth: 1,
          width: '100%',
          cursor: 'default',
        }}
      >
        <Stack
          sx={{
            padding: 1.5,
            borderRadius: 1,
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '32px',
            backgroundColor: 'primary.action.selected',
          }}
        >
          <AttachFileOutlined sx={{ color: 'primary.main', fontSize: '20px' }} />
        </Stack>

        <Stack>
          <Typography variant="labelSmall">{attachment.name}</Typography>
          <Typography variant="caption" color="secondary">
            {attachment.contentType}
          </Typography>
        </Stack>
      </Stack>

      <IconButton variant="outlined" color="primary" onClick={onPreview}>
        <RemoveRedEyeOutlinedIcon />
      </IconButton>

      <IconButton variant="outlined" color="secondary" onClick={onDownload}>
        <GetAppOutlinedIcon />
      </IconButton>
    </Stack>
  );
};

export const CustomerJobDetailsField: FC<{ title: string; value: string; disabled?: boolean }> = ({
  title,
  value,
  disabled,
}) => (
  <Stack direction="column" flex={1}>
    <Typography variant="body2" color="textSecondary">
      {title}
    </Typography>
    <Typography
      data-testname={`${title}-value`}
      variant="body1"
      color={disabled ? 'textSecondary' : 'textPrimary'}
    >
      {value}
    </Typography>
  </Stack>
);

interface Props {
  open: boolean;
  job?: Entity<CustomerJob>;
  onClose: () => void;
}

const ModalContent: FC<{ job: Entity<CustomerJob> }> = ({ job }) => {
  const t = useTranslationPrefix('customer_portal.requested_service_details_modal');
  return (
    <Stack direction="column">
      <Grid container spacing={4}>
        <Grid item xs={6}>
          <Stack
            spacing={4}
            direction="column"
            sx={{
              borderRight: '1px solid gray',
              borderRightColor: (theme) => theme.palette.divider,
            }}
          >
            <Typography variant="h6">{t('section_primary')}</Typography>
            <CustomerJobDetailsField title={t('airport_label')} value={job.airport.name} />
            <CustomerJobDetailsField title={t('facility_label')} value={job.facility.name} />
            <CustomerJobDetailsField title={t('customer_label')} value={job.customer.name} />
            {job.type === 'request' &&
              (job.jobRequest?.specialServiceNote ? (
                <>
                  <Typography variant="h6">{t('section_special_service')}</Typography>
                  <CustomerJobDetailsField
                    title={t('special_service_notes_label')}
                    value={job.jobRequest?.specialServiceNote}
                  />
                </>
              ) : (
                <>
                  <Typography variant="h6">{t('section_aircraft')}</Typography>
                  <CustomerJobDetailsField
                    title={t('aircraft_reg_label')}
                    value={job.jobRequest?.aircraftRegNumber || 'n/a'}
                  />
                  <CustomerJobDetailsField
                    title={t('aircraft_serial_label')}
                    value={job.jobRequest?.aircraftSerialNumber || 'n/a'}
                  />
                  <CustomerJobDetailsField
                    title={t('aircraft_type_label')}
                    value={job.jobRequest?.aircraftType?.name || 'n/a'}
                  />
                </>
              ))}
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <Stack spacing={4} direction="column">
            <Typography variant="h6">{t('section_services')}</Typography>
            {job.requestedServices.map((s, ndx) => (
              <CustomerJobDetailsField
                key={'service_' + ndx}
                title={t('service_no', { no: ndx + 1 })}
                value={s.customerName}
              />
            ))}
            {job.jobRequest && (
              <>
                <Divider />

                <CustomerJobDetailsField
                  title={t('notes_label')}
                  value={job.jobRequest?.serviceNeeds || 'n/a'}
                ></CustomerJobDetailsField>
              </>
            )}
            <Divider />

            <Typography variant="h6">{t('section_attachments')}</Typography>
            {job.attachments.length > 0 ? (
              job.attachments.map((attachment) => (
                <AttachmentItem
                  key={attachment.uri}
                  attachment={attachment}
                  onPreview={() => {
                    window.open(attachment.uri, '_blank');
                  }}
                  onDownload={() => {
                    downloadFile(attachment.uri, attachment.name);
                  }}
                />
              ))
            ) : (
              <NoAttachments />
            )}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

const RequestedServiceDetailsModal: FC<Props> = ({ open, onClose, job }) => {
  const t = useTranslationPrefix('customer_portal.requested_service_details_modal');

  return (
    <Modal
      title={t('title')}
      open={open}
      fullWidth
      maxWidth="md"
      onClose={onClose}
      showCloseButton
      content={job ? <ModalContent job={job} /> : <Stack />}
    ></Modal>
  );
};

export default RequestedServiceDetailsModal;
